import * as React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { canEditDomainCompliance } from '../../domainCompliancePermissions';
import { useDomainApprovalsQuery, useDomainComplianceMutations } from '../../hooks';
import {
  DomainComplianceDemandPartner,
  DomainComplianceStatus,
  isDomainComplianceDemandPartner,
  serializeDomainApprovals,
  type DomainComplianceApproval,
  type DomainComplianceSummary,
} from '../../model';
import { formatDisplayDate } from '../../../../../lib/format';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';

interface DomainComplianceFormValue {
  approvals: DomainComplianceApproval[];
}

export function DomainComplianceDialog({
  domain,
  onOpenChange,
}: {
  domain: DomainComplianceSummary | null;
  onOpenChange: (open: boolean) => void;
}) {
  'use no memo';

  const { can } = useShigoPermissions();
  const canSave = canEditDomainCompliance(can);
  const query = useDomainApprovalsQuery(domain?.url);
  const mutations = useDomainComplianceMutations();
  const pending = mutations.updateApprovals.isPending;
  const guard = useBusyDialogGuard(pending, () => {
    onOpenChange(false);
  });
  const form = useForm<DomainComplianceFormValue>({ defaultValues: { approvals: [] } });
  const { fields } = useFieldArray({ control: form.control, name: 'approvals' });
  const { isDirty } = form.formState;
  const loadedDomainRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!domain) {
      loadedDomainRef.current = null;
      return;
    }
    if (!query.data) return;
    const domainChanged = loadedDomainRef.current !== domain.url;
    if (domainChanged || !isDirty) {
      form.reset({ approvals: query.data });
      loadedDomainRef.current = domain.url;
    }
  }, [domain, form, isDirty, query.data]);

  async function submit(value: DomainComplianceFormValue) {
    if (!canSave) return;
    try {
      await mutations.updateApprovals.mutateAsync(serializeDomainApprovals(value.approvals));
      toast.success(dashboardT('supplyDomains.updateSuccess'));
      onOpenChange(false);
    } catch (error) {
      showRequestError(error, dashboardT('supplyDomains.updateError'));
    }
  }

  return (
    <Dialog open={Boolean(domain)} onOpenChange={guard.onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-[min(1300px,calc(100vw-2rem))] gap-0 overflow-hidden p-0"
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-6 py-4">
          <DialogTitle>{dashboardT('supplyDomains.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {domain
              ? dashboardT('supplyDomains.dialogDescription', {
                  publisher: domain.publisherName,
                  domain: domain.url,
                })
              : ''}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(event) => {
              void form.handleSubmit(submit)(event);
            }}
          >
            <div className="min-h-0 flex-1 overflow-auto">
              {query.isLoading ? (
                <div
                  className="space-y-3 p-6"
                  aria-label={dashboardT('supplyDomains.approvalsLoading')}
                  role="status"
                >
                  {Array.from({ length: 4 }, (_, index) => (
                    <Skeleton key={String(index)} className="h-12 w-full" />
                  ))}
                </div>
              ) : null}
              {query.isError ? (
                <div className="flex min-h-56 items-center justify-center p-6">
                  <p role="alert" className="text-muted-foreground text-sm">
                    {dashboardT('supplyDomains.approvalsLoadError')}
                  </p>
                </div>
              ) : null}
              {!query.isLoading && !query.isError && fields.length === 0 ? (
                <div className="flex min-h-56 items-center justify-center p-6">
                  <p className="text-muted-foreground text-sm">
                    {dashboardT('supplyDomains.approvalsEmpty')}
                  </p>
                </div>
              ) : null}
              {!query.isLoading && !query.isError && fields.length ? (
                <Table className="min-w-[1120px] table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-44 pl-4">
                        {dashboardT('supplyDomains.dpName')}
                      </TableHead>
                      <TableHead className="w-24">{dashboardT('supplyDomains.allowed')}</TableHead>
                      <TableHead className="w-40">{dashboardT('supplyDomains.status')}</TableHead>
                      <TableHead className="w-72">{dashboardT('supplyDomains.dpIds')}</TableHead>
                      <TableHead className="w-28">
                        {dashboardT('supplyDomains.submitted')}
                      </TableHead>
                      <TableHead className="w-36">
                        {dashboardT('supplyDomains.submittedDate')}
                      </TableHead>
                      <TableHead className="w-64 pr-4">
                        {dashboardT('supplyDomains.comment')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fields.map((field, index) => (
                      <ApprovalRow
                        key={field.id}
                        approval={field}
                        index={index}
                        disabled={!canSave || pending}
                        control={form.control}
                        register={form.register}
                        setValue={form.setValue}
                      />
                    ))}
                  </TableBody>
                </Table>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-6 py-4">
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={pending}>
                  {dashboardT('supplyDomains.close')}
                </Button>
              </DialogClose>
              {canSave ? (
                <Button type="submit" disabled={pending || query.isLoading || query.isError}>
                  {pending ? dashboardT('supplyDomains.saving') : dashboardT('supplyDomains.save')}
                </Button>
              ) : null}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

type ApprovalRowProps = {
  approval: DomainComplianceApproval;
  index: number;
  disabled: boolean;
  control: ReturnType<typeof useForm<DomainComplianceFormValue>>['control'];
  register: ReturnType<typeof useForm<DomainComplianceFormValue>>['register'];
  setValue: ReturnType<typeof useForm<DomainComplianceFormValue>>['setValue'];
};

function ApprovalRow({ approval, index, disabled, control, register, setValue }: ApprovalRowProps) {
  const displayName = approval.demandPartner.displayName;
  const showMagniteIds = isDomainComplianceDemandPartner(
    approval,
    DomainComplianceDemandPartner.Magnite,
  );
  const showPubmaticIds = isDomainComplianceDemandPartner(
    approval,
    DomainComplianceDemandPartner.Pubmatic,
  );

  return (
    <TableRow>
      <TableCell className="pl-4 font-medium">{displayName}</TableCell>
      <TableCell>
        <Controller
          control={control}
          name={approvalFieldName(index, 'allowed')}
          render={({ field }) => (
            <Switch
              aria-label={dashboardT('supplyDomains.allowedFor', { name: displayName })}
              checked={field.value === true}
              disabled={disabled}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <Controller
          control={control}
          name={approvalFieldName(index, 'status')}
          render={({ field }) => (
            <Select value={field.value} disabled={disabled} onValueChange={field.onChange}>
              <SelectTrigger
                aria-label={dashboardT('supplyDomains.statusFor', { name: displayName })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions(field.value).map((status) => (
                  <SelectItem
                    key={status}
                    value={status}
                    disabled={status === DomainComplianceStatus.Unknown}
                  >
                    {statusLabel(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </TableCell>
      <TableCell>
        {showMagniteIds || showPubmaticIds ? (
          <div className="grid grid-cols-3 gap-2">
            <Input
              aria-label={dashboardT('supplyDomains.siteId')}
              placeholder={dashboardT('supplyDomains.siteId')}
              disabled={disabled}
              {...register(approvalFieldName(index, 'dppSiteId'))}
            />
            {showMagniteIds ? (
              <Input
                aria-label={dashboardT('supplyDomains.zoneId')}
                placeholder={dashboardT('supplyDomains.zoneId')}
                disabled={disabled}
                {...register(approvalFieldName(index, 'dppZoneId'))}
              />
            ) : null}
            {showPubmaticIds ? (
              <>
                <Input
                  aria-label={dashboardT('supplyDomains.publisherId')}
                  placeholder={dashboardT('supplyDomains.publisherId')}
                  disabled={disabled}
                  {...register(approvalFieldName(index, 'dppPublisherId'))}
                />
                <Input
                  aria-label={dashboardT('supplyDomains.tagId')}
                  placeholder={dashboardT('supplyDomains.tagId')}
                  disabled={disabled}
                  {...register(approvalFieldName(index, 'dppTagId'))}
                />
              </>
            ) : null}
          </div>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>
        <Controller
          control={control}
          name={approvalFieldName(index, 'submitted')}
          render={({ field }) => (
            <Switch
              aria-label={dashboardT('supplyDomains.submittedFor', { name: displayName })}
              checked={field.value === true}
              disabled={disabled}
              onCheckedChange={(checked) => {
                field.onChange(checked);
                setValue(
                  approvalFieldName(index, 'submittedOn'),
                  checked ? localDate(new Date()) : null,
                  { shouldDirty: true },
                );
              }}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <Controller
          control={control}
          name={approvalFieldName(index, 'submittedOn')}
          render={({ field }) => <span>{formatDate(field.value)}</span>}
        />
      </TableCell>
      <TableCell className="pr-4">
        <Input
          aria-label={dashboardT('supplyDomains.commentFor', { name: displayName })}
          placeholder={dashboardT('supplyDomains.comment')}
          disabled={disabled}
          {...register(approvalFieldName(index, 'comment'))}
        />
      </TableCell>
    </TableRow>
  );
}

function statusOptions(current: DomainComplianceStatus) {
  const known = [
    DomainComplianceStatus.Approved,
    DomainComplianceStatus.Pending,
    DomainComplianceStatus.Rejected,
    DomainComplianceStatus.NotAvailable,
  ];
  return current === DomainComplianceStatus.Unknown
    ? [...known, DomainComplianceStatus.Unknown]
    : known;
}

function statusLabel(status: DomainComplianceStatus) {
  const keys = {
    [DomainComplianceStatus.Approved]: 'supplyDomains.status.approved',
    [DomainComplianceStatus.Pending]: 'supplyDomains.status.pending',
    [DomainComplianceStatus.Rejected]: 'supplyDomains.status.rejected',
    [DomainComplianceStatus.NotAvailable]: 'supplyDomains.status.notAvailable',
    [DomainComplianceStatus.Unknown]: 'supplyDomains.status.unknown',
  } as const;
  return dashboardT(keys[status]);
}

function localDate(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${String(year)}-${month}-${day}`;
}

function formatDate(value: string | null | undefined) {
  // A bare YYYY-MM-DD is a local calendar day, not UTC midnight.
  const dateOnly = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  return formatDisplayDate(
    dateOnly ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])) : value,
  );
}

type EditableApprovalField =
  | 'allowed'
  | 'status'
  | 'submitted'
  | 'submittedOn'
  | 'comment'
  | 'dppSiteId'
  | 'dppZoneId'
  | 'dppPublisherId'
  | 'dppTagId';

function approvalFieldName<TField extends EditableApprovalField>(
  index: number,
  field: TField,
): `approvals.${number}.${TField}` {
  return `approvals.${String(index)}.${field}` as `approvals.${number}.${TField}`;
}
